import {
  type FloorPlanDocument,
  type FloorPlanPoint,
  type FloorPlanTable,
  tablePolygon,
} from "@/lib/shows/floorPlan";

export type PlanTableAssignment = {
  tableId: string;
  participationId: string;
  vendorName?: string;
  searchable?: boolean;
};

export type FloorPlanDiff = {
  addedTableIds: string[];
  movedTableIds: string[];
  removedTableIds: string[];
  affectedParticipationIds: string[];
  affectedVendorNames: string[];
};

function tableFingerprint(table: FloorPlanTable): string {
  return JSON.stringify([
    table.number,
    table.center.x,
    table.center.y,
    table.width,
    table.depth,
    table.rotation,
    table.clearance?.front,
    table.clearance?.back,
    table.clearance?.side,
  ]);
}

export function diffFloorPlans(
  previous: FloorPlanDocument,
  next: FloorPlanDocument,
  previousAssignments: readonly PlanTableAssignment[] = [],
  nextAssignments: readonly PlanTableAssignment[] = previousAssignments,
): FloorPlanDiff {
  const previousById = new Map(previous.tables.map((table) => [table.id, table]));
  const nextById = new Map(next.tables.map((table) => [table.id, table]));
  const addedTableIds = next.tables.filter((table) => !previousById.has(table.id)).map((table) =>
    table.id
  );
  const movedTableIds = next.tables.filter((table) => {
    const before = previousById.get(table.id);
    return before !== undefined && tableFingerprint(before) !== tableFingerprint(table);
  }).map((table) => table.id);
  const removedTableIds = previous.tables.filter((table) => !nextById.has(table.id)).map((table) =>
    table.id
  );
  const changed = new Set([...addedTableIds, ...movedTableIds, ...removedTableIds]);
  const affectedParticipationIds = new Set<string>();
  const affectedVendorNames = new Set<string>();
  for (const assignment of [...previousAssignments, ...nextAssignments]) {
    if (!changed.has(assignment.tableId)) continue;
    affectedParticipationIds.add(assignment.participationId);
    if (assignment.vendorName) affectedVendorNames.add(assignment.vendorName);
  }
  return {
    addedTableIds,
    movedTableIds,
    removedTableIds,
    affectedParticipationIds: [...affectedParticipationIds].sort(),
    affectedVendorNames: [...affectedVendorNames].sort(),
  };
}

export type PlanPrintOptions = {
  plan: FloorPlanDocument;
  title: string;
  revision: number;
  publishedAt: string;
  variant: "public" | "internal";
  assignments?: readonly PlanTableAssignment[];
  draft?: boolean;
};

function escapeHtml(value: string | number): string {
  return String(value).replace(/[&<>"']/g, (character) =>
    ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    })[character] ?? character);
}

function points(polygon: readonly FloorPlanPoint[]): string {
  return polygon.map((point) => `${point.x},${point.y}`).join(" ");
}

function viewBox(plan: FloorPlanDocument): string {
  const all = [
    ...plan.boundary,
    ...plan.areas.flatMap((area) => area.polygon),
    ...plan.tables.flatMap(tablePolygon),
  ];
  if (!all.length) return "0 0 1 1";
  const xs = all.map((point) => point.x);
  const ys = all.map((point) => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const width = Math.max(1, Math.max(...xs) - minX);
  const height = Math.max(1, Math.max(...ys) - minY);
  const padding = Math.max(width, height) * 0.03;
  return `${minX - padding} ${minY - padding} ${width + 2 * padding} ${height + 2 * padding}`;
}

/** Builds a browser-printable vector document; the caller opens it and invokes window.print(). */
export function buildPlanPrintHtml({
  plan,
  title,
  revision,
  publishedAt,
  variant,
  assignments = [],
  draft = false,
}: PlanPrintOptions): string {
  const assignmentByTable = new Map(
    assignments.map((assignment) => [assignment.tableId, assignment]),
  );
  const areas = plan.areas.map((area) =>
    `<polygon points="${points(area.polygon)}" class="area ${
      area.obstructs ? "obstructing" : "wayfinding"
    }"/>`
  ).join("");
  const tables = plan.tables.map((table) => {
    const assignment = assignmentByTable.get(table.id);
    const label = variant === "internal" && assignment?.vendorName
      ? `${escapeHtml(table.number)} · ${escapeHtml(assignment.vendorName)}`
      : escapeHtml(table.number);
    return `<g class="table ${assignment ? "assigned" : "unassigned"}"><polygon points="${
      points(tablePolygon(table))
    }"/><text x="${table.center.x}" y="${table.center.y}" text-anchor="middle" dominant-baseline="middle">${label}</text></g>`;
  }).join("");
  const stamp = draft
    ? `Revision ${revision} · Draft printed ${publishedAt}`
    : `Revision ${revision} · Published ${publishedAt}`;

  return `<!doctype html><html><head><meta charset="utf-8"><title>${
    escapeHtml(title)
  } floor plan</title><style>
@page{size:landscape;margin:12mm}*{box-sizing:border-box}body{margin:0;color:#111;font:14px Arial,sans-serif}header{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:8px}h1{margin:0;font-size:20px}.stamp{font-size:11px}svg{display:block;width:100%;height:calc(100vh - 48px)}.boundary{fill:#fff;stroke:#111;stroke-width:14}.area{fill:none;stroke:#555;stroke-width:7;stroke-dasharray:18 12}.obstructing{fill:#ddd;stroke:#222;stroke-dasharray:none}.table polygon{fill:#fff;stroke:#111}.table.assigned polygon{stroke-width:13}.table.unassigned polygon{stroke-width:6}.table text{fill:#111;font-size:52px;font-weight:700;paint-order:stroke;stroke:#fff;stroke-width:8px;stroke-linejoin:round}.watermark{position:fixed;inset:45% 0 auto;text-align:center;transform:rotate(-18deg);font-size:84px;font-weight:700;letter-spacing:.2em;color:rgba(0,0,0,.13);pointer-events:none}
</style></head><body><header><h1>${escapeHtml(title)}</h1><div class="stamp">${
    escapeHtml(stamp)
  }</div></header>${
    draft ? '<div class="watermark">DRAFT</div>' : ""
  }<svg xmlns="http://www.w3.org/2000/svg" viewBox="${
    viewBox(plan)
  }" preserveAspectRatio="xMidYMid meet"><polygon points="${
    points(plan.boundary)
  }" class="boundary"/>${areas}${tables}</svg></body></html>`;
}
